import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IRootState } from 'Scripts/store';
import { useOrderSearchMutation, Order } from 'Services/orderSearchApi';

import Loader from 'Components/Loader';
import MagnicifactionGlassIcon from 'Icons/magnificationGlass.svg?react';
import CtaButton from 'Components/CtaButton';
import OrderStatus from '../OrderStatus';
import useImpersonateUser from '../../Hooks/useImpersonateUser';

const OrderListing = ({
    selectOrder,
    returnOrders = false,
}: {
    selectOrder: (id: string) => void;
    returnOrders?: boolean;
}) => {
    const { currentUser, isImpersonating } = useImpersonateUser();

    const { translations } = useSelector(
        (state: IRootState) => state?.cart?.miniCartModel
    );

    const [selectedOrder, setSelectedOrder] = useState<Order>(null);
    const [orderSearchQuery, setOrderSearchQuery] = useState('');
    const [prevSearchQuery, setPrevSearchQuery] = useState('');
    const [showLoading, setShowLoading] = useState(false);
    const [orderResultList, setOrderResultList] = useState<Order[]>([]);
    const [
        getOrderSearchResult,
        {
            data: orderSearchResult,
            isSuccess: searchSuccess,
            isLoading: searchLoading,
            isError: searchError,
        },
    ] = useOrderSearchMutation();

    useEffect(() => {
        if (searchLoading) {
            setShowLoading(true);
        }
        if (searchSuccess) {
            setPrevSearchQuery(orderSearchQuery);
            setShowLoading(false);
            setSelectedOrder('');
            setOrderResultList(
                [...orderSearchResult].sort((a: Order, b: Order) => {
                    const dateA = new Date(a.orderDate);
                    const dateB = new Date(b.orderDate);
                    return dateB.getTime() - dateA.getTime(); // Descending order
                })
            );
        }
        if (searchError) {
            setShowLoading(false);
        }
    }, [searchLoading, searchSuccess, searchError]);

    const searchOrders = () => {
        if (!orderSearchQuery || orderSearchQuery == prevSearchQuery) {
            return;
        }

        getOrderSearchResult({
            query: orderSearchQuery,
            searchReturnOrders: returnOrders || false,
        });
    };

    const searchOrdersOnEnterKey = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            searchOrders();
        }
    };

    const formatDateTime = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    const isOrderSelectable = (order: Order) => {
        const status = order.status.toLowerCase();
        return (
            status !== 'completed' &&
            status !== 'cancelled' &&
            status !== 'released' &&
            status !== 'notexchangeable' &&
            status !== 'exchanged'
        );
    };

    const handleOrderSelectionConfirmed = () => {
        if (!selectedOrder) {
            return;
        }

        const message = `${translations.customerServiceOrderCustomerMismatch}
${translations.customerServiceOrderNumber}: ${selectedOrder.orderNumber}
${translations.customerServiceCustomer}: ${selectedOrder.firstName} ${selectedOrder.lastName}`;

        if (
            isImpersonating &&
            currentUser?.id !== selectedOrder.customerNumber &&
            !window.confirm(message)
        ) {
            return;
        }

        selectOrder(selectedOrder);
    };

    const handleOrderSelect = (order: Order) => {
        if (!isOrderSelectable(order)) return;

        if (selectedOrder?.orderNumber === order.orderNumber) {
            handleOrderSelectionConfirmed();
        } else {
            setSelectedOrder(order);
        }
    };

    return (
        <>
            <div className="border-t border-[#f8f5f0] px-6 pb-6 pt-3.5 pt-6 leading-4 shadow-[0px_5px_16px_0px_rgba(0,0,0,0.08)]">
                <span className="mb-1 text-sm">
                    {translations.customerServiceSearchOrder}
                </span>
                <div className="relative flex gap-1.5">
                    <input
                        autoFocus
                        className="relative h-10 grow rounded-sm border px-3.5 py-2 text-base focus-within:border-[#015ECC] focus-within:outline-0"
                        type="search"
                        onKeyDown={searchOrdersOnEnterKey}
                        onChange={e =>
                            setOrderSearchQuery(e.currentTarget.value.trim())
                        }
                    />
                    <button
                        className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-sm bg-black text-white outline-[#015ECC]"
                        onClick={searchOrders}
                        onKeyDown={searchOrdersOnEnterKey}
                    >
                        <MagnicifactionGlassIcon className="h-6 text-current" />
                    </button>
                </div>
            </div>

            <div className="scroll-bar-gsd::-webkit-scrollbar scroll-bar-gsd::-webkit-scrollbar-thumb scroll-bar-gsd flex grow flex-col gap-1 overflow-y-auto bg-[#F8F5F0]">
                <table className="w-full bg-white">
                    <thead className="sticky top-0 z-10 bg-gray-50">
                        <tr className="border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                {translations.customerServiceOrderNumber}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                {translations.customerServiceOrderDate}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                {translations.customerServiceOrderLastName}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                {translations.customerServiceOrderFirstName}
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                                {translations.customerServiceOrderStatus}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                {translations.customerServiceOrderTotal}
                            </th>
                        </tr>
                    </thead>

                    {showLoading && (
                        <tbody>
                            <tr>
                                <td
                                    colSpan={6}
                                    className="px-6 py-4 text-center"
                                >
                                    <Loader size="10px" marginY="my-2" />
                                </td>
                            </tr>
                        </tbody>
                    )}
                    {searchSuccess &&
                        orderResultList &&
                        orderResultList.length === 0 && (
                            <tbody>
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-6 py-4 text-center text-sm text-gray-500"
                                    >
                                        {
                                            translations.customerServiceOrderSearchEmpty
                                        }
                                    </td>
                                </tr>
                            </tbody>
                        )}

                    {!showLoading && orderResultList.length > 0 && (
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {orderResultList.map((order: Order) => (
                                <tr
                                    key={order.orderNumber}
                                    className={`cursor-pointer transition-colors ${
                                        isOrderSelectable(order)
                                            ? selectedOrder?.orderNumber ===
                                              order.orderNumber
                                                ? 'bg-[#ebd4d8]'
                                                : 'hover:bg-gray-50'
                                            : 'cursor-not-allowed opacity-60'
                                    }`}
                                    onClick={() => handleOrderSelect(order)}
                                >
                                    <td className="px-4 py-4 text-sm font-medium text-gray-900">
                                        {order.orderNumber}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-500">
                                        {formatDateTime(order.orderDate)}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-500">
                                        {order.lastName}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-500">
                                        {order.firstName}
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                        <OrderStatus
                                            translations={translations}
                                            orderStatus={order.status}
                                        />
                                    </td>
                                    <td className="px-4 py-4 text-sm font-medium text-gray-900">
                                        {order.orderTotal}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>

            <div className="w-full bg-white px-4.5 py-2.5 shadow-[0px_-5px_16px_0px_rgba(0,0,0,0.05)]">
                <CtaButton
                    as="button"
                    ctaType="primary"
                    className={`w-full text-center font-semibold ${
                        !selectedOrder ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                    disabled={!selectedOrder}
                    onClick={handleOrderSelectionConfirmed}
                >
                    {translations.customerServiceSelectOrder}
                </CtaButton>
            </div>
        </>
    );
};

export default OrderListing;
